<script setup>
import { computed, onMounted, ref } from 'vue'
import { api } from '../api'
import { useI18n } from '../i18n'
import { session } from '../session'

const { t } = useI18n()
const isAdmin = computed(() => session.user?.role === 'ADMIN')
const stations = ref([])
const loading = ref(true)
const saving = ref(false)
const testingId = ref(null)
const deletingId = ref(null)
const error = ref('')
const notice = ref('')
const form = ref({
  displayName: t('defaultStationName'), host: '127.0.0.1', port: 3306,
  databaseName: '', username: '', password: '', enabled: true,
})

async function load() {
  loading.value = true
  try {
    stations.value = (await api('/admin/stations')).stations
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

async function createStation() {
  error.value = ''
  notice.value = ''
  saving.value = true
  try {
    const result = await api('/admin/stations', { method: 'POST', body: JSON.stringify(form.value) })
    stations.value = [...stations.value, result.station].sort((left, right) => left.displayName.localeCompare(right.displayName))
    notice.value = t('stationCreated')
  } catch (cause) {
    error.value = cause.message
  } finally {
    saving.value = false
  }
}

async function testStation(station) {
  error.value = ''
  notice.value = ''
  testingId.value = station.id
  try {
    const result = await api(`/admin/stations/${station.id}/test`, { method: 'POST', body: '{}' })
    notice.value = t('stationTested', { name: station.displayName, elapsed: result.elapsedMs })
  } catch (cause) {
    error.value = cause.message
  } finally {
    testingId.value = null
  }
}

async function deleteStation(station) {
  if (!window.confirm(t('deleteStationConfirm', { name: station.displayName }))) return
  error.value = ''
  notice.value = ''
  deletingId.value = station.id
  try {
    await api(`/admin/stations/${station.id}`, { method: 'DELETE' })
    stations.value = stations.value.filter((item) => item.id !== station.id)
    notice.value = t('stationDeleted', { name: station.displayName })
  } catch (cause) {
    error.value = cause.message
  } finally {
    deletingId.value = null
  }
}

function stationStatus(station) {
  return station.enabled ? t('active') : t('disabled')
}

onMounted(load)
</script>

<template>
  <section>
    <div class="page-title"><div><p class="eyebrow">{{ t('settings') }}</p><h1>{{ t('stationsTitle') }}</h1></div></div>
    <p class="muted">{{ t('stationsHint') }}</p>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p>
    <p v-if="notice" class="notice success">{{ notice }}</p>
    <p v-if="!isAdmin" class="notice info" role="status">{{ t('viewOnlyHint') }}</p>
    <div :class="isAdmin ? 'two-columns' : 'single-column'">
      <section v-if="isAdmin" class="card">
        <h2>{{ t('addStation') }}</h2>
        <form class="form-stack" @submit.prevent="createStation">
          <label>{{ t('stationName') }} <input v-model="form.displayName" maxlength="120" required /></label>
          <div class="field-row"><label>{{ t('host') }} <input v-model="form.host" maxlength="255" required /></label><label>{{ t('port') }} <input v-model.number="form.port" type="number" min="1" max="65535" required /></label></div>
          <label>{{ t('databaseName') }} <input v-model="form.databaseName" maxlength="128" required /></label>
          <label>{{ t('databaseUser') }} <input v-model="form.username" maxlength="128" required /></label>
          <label>{{ t('databasePassword') }} <input v-model="form.password" type="password" maxlength="1024" /></label>
          <label class="checkbox"><input v-model="form.enabled" type="checkbox" /> {{ t('managerAvailable') }}</label>
          <button class="button" :disabled="saving" type="submit">{{ saving ? t('saving') : t('createProfile') }}</button>
        </form>
      </section>
      <section class="card">
        <h2>{{ t('configuredStations') }}</h2>
        <p v-if="loading" class="muted">{{ t('loading') }}</p>
        <div v-else-if="stations.length" class="table-wrap"><table><thead><tr><th>{{ t('stations') }}</th><th>{{ t('connection') }}</th><th>{{ t('status') }}</th><th v-if="isAdmin"></th></tr></thead><tbody>
          <tr v-for="station in stations" :key="station.id"><td>{{ station.displayName }}</td><td>{{ station.host }}:{{ station.port }}/{{ station.databaseName }}</td><td>{{ stationStatus(station) }}</td><td v-if="isAdmin" class="station-actions"><button class="button button-small" type="button" :disabled="testingId === station.id || deletingId === station.id" @click="testStation(station)">{{ testingId === station.id ? t('testing') : t('test') }}</button><button class="button button-small button-danger" type="button" :disabled="testingId === station.id || deletingId === station.id" @click="deleteStation(station)">{{ deletingId === station.id ? t('deleting') : t('delete') }}</button></td></tr>
        </tbody></table></div>
        <p v-else class="muted">{{ t('noProfiles') }}</p>
      </section>
    </div>
  </section>
</template>
